/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { routePaths } from '@/constants/routePaths';
import { Skeleton, View } from '@az/starc-ui';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { CMSYmme } from '@/features/contentstack/components/CMSYmme/CMSYmme';
import styles from './CMSPDPYmme.module.scss';
import { NewCertonaForOos } from '@/components/ProductDetail/NewCertonaForOos';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useIsStoreSelected } from '@/features/header/hooks/useIsStoreSelected';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { type CertonaRecommendation, type AddCertonaItemObjectType } from '@/features/certona';
import { useFeatureFlag } from '@/features/globalConfig';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { DoesNotFitNotification } from '@/components/ProductDetail/NewCertonaForOos/DoesNotFitNotification';
import { pageTypes } from '@/constants/page';
import { keepPreviousData } from '@tanstack/react-query';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useState } from 'react';

type Props = {
  certonaRecommendations?: CertonaRecommendation[];
  certonaLength?: boolean;
  isFetchingNewContainerAvailability?: boolean;
  shouldShowLoadingSkeleton?: boolean;
  setCertonaRecommendationAddedToCart: React.Dispatch<React.SetStateAction<boolean>>;
  setAddedCertonaItemObject: React.Dispatch<React.SetStateAction<AddCertonaItemObjectType | null>>;
};

export function CMSPDPYmme({
  certonaRecommendations,
  shouldShowLoadingSkeleton,
  setCertonaRecommendationAddedToCart,
  setAddedCertonaItemObject,
}: Props) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [skeletonHeight, setSkeletonHeight] = useState<string>(isMobile ? '100%' : '115px');

  const isCertonaEnabledForOosOrDoesNotFit =
    useFeatureFlag('IS_OOS_OR_DOES_NOT_FIT_CONTAINER_ENABLED') === 'true';
  const { acesEnabled } = useAcesFlags();

  const { data: headerData } = useHeaderData();
  const isVehicleSelected =
    Boolean(headerData?.vehicleMap?.vehicleId) || Boolean(headerData?.vehicleMap?.modelId);

  const { setPageSpecificData } = usePageSpecificData();
  const hasStoreSelected = useIsStoreSelected();
  const {
    data: productDetailsData,
    isSuccess: isProductDetailsSuccess,
    isFetching: isProductDetailsFetching,
    isPlaceholderData: isProductDetailsPlaceholder,
  } = useProductDetails({
    placeholderData: keepPreviousData,
  });
  const { recordType, breadCrumbs, itemId, vehicleFitFlag } = productDetailsData?.product ?? {};
  const vehicleFit =
    productDetailsData?.product && 'vehicleFit' in productDetailsData.product
      ? productDetailsData.product.vehicleFit
      : '';
  const vehicleFitment =
    productDetailsData?.product && 'vehicleFitment' in productDetailsData.product
      ? productDetailsData.product.vehicleFitment
      : '';
  const {
    data: skuDetailsData,
    isSuccess: isProductSkuDetailsSuccess,
    isFetching: isProductSkuDetailsFetching,
  } = useProductSkuDetails({
    skuIds: itemId ? [itemId] : [],
    enabled: Boolean(itemId),
  });
  const itemFulfillmentOptions =
    skuDetailsData?.[0]?.skuPricingAndAvailability?.skuAvailabilityInfo?.fulfillmentOptions;
  const seePartsThatFitHref =
    breadCrumbs?.find(
      (item) => item.pageType === 'ProductShelf' || (acesEnabled && item.pageType === 'PartGroup'),
    )?.seoUrl ?? routePaths.blankURL;

  // TODO - update vehicleFit values after BE changes
  const isAppProduct = recordType === 'app' || vehicleFitment === 'VEHICLE_SPECIFIC';
  const isAppProductThatDoesNotFit =
    isAppProduct && (acesEnabled ? vehicleFit === 'DOES_NOT_FIT' : !vehicleFitFlag); // ACES : Parts Pro
  const isOos =
    hasStoreSelected &&
    (isVehicleSelected || (!isVehicleSelected && !isAppProduct)) &&
    itemFulfillmentOptions?.some((item) => item.available || item.sisterStoreAvailable) === false;
  const needsMoreInfo = acesEnabled && vehicleFit === 'NEEDS_MORE_INFO';
  const doesNotFit = hasStoreSelected && isVehicleSelected && isAppProductThatDoesNotFit;
  const showYmme = (!isVehicleSelected && isAppProduct) || needsMoreInfo;
  const showTopComponent = isAppProductThatDoesNotFit || needsMoreInfo || isOos || showYmme;
  const productDetailsIsFetched =
    !isProductDetailsFetching &&
    isProductDetailsSuccess &&
    productDetailsData !== undefined &&
    !isProductDetailsPlaceholder;
  const productSkuDetailsIsFetched = !isProductSkuDetailsFetching && isProductSkuDetailsSuccess;

  const logAnalytics = () => {
    setPageSpecificData({
      productFindingMethod: doesNotFit ? 'Finder' : 'Product Toast Notification',
      pfmLocation: 'Product Toast Notification',
      eventType: 'productToastNotificationClick',
      ...(doesNotFit && {
        finderFeature: 'Parts that Fit Banner',
      }),
    });
  };

  if (productDetailsIsFetched && productSkuDetailsIsFetched && showTopComponent) {
    if (shouldShowLoadingSkeleton) {
      return (
        <View className={styles.removeHorizontalPadding} height={skeletonHeight}>
          <Skeleton height="100%" width="100%" />
        </View>
      );
    } else if (showYmme || needsMoreInfo) {
      return (
        <View className={styles.ymmePadding}>
          <CMSYmme
            removeMargins
            pageType={pageTypes.ProductDetail}
            onHeightChange={setSkeletonHeight}
          />
        </View>
      );
    } else if (isCertonaEnabledForOosOrDoesNotFit && (isOos || doesNotFit)) {
      return (
        <NewCertonaForOos
          isOos={isOos}
          doesNotFit={doesNotFit}
          logAnalytics={logAnalytics}
          certonaRecommendations={certonaRecommendations}
          setCertonaRecommendationAddedToCart={setCertonaRecommendationAddedToCart}
          setAddedCertonaItemObject={setAddedCertonaItemObject}
        />
      );
    } else if (isAppProductThatDoesNotFit) {
      return (
        <DoesNotFitNotification href={seePartsThatFitHref} onHeightChange={setSkeletonHeight} />
      );
    }
  }

  return null;
}
